/**
 * EnvironmentGraphPage — Force-directed knowledge graph surface.
 *
 * Lives at /environment. Wired to backend knowledge-graph endpoint.
 * Types match backend/schemas/environment_graph.py exactly.
 *
 * Cycle 074.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  PhaseTabs,
  type PhaseId,
} from '../components/environment/canvas/PhaseTabs';
import {
  PhaseOverlay,
  type PhaseOverlayData,
} from '../components/environment/canvas/PhaseOverlay';
import {
  PHASE_FROM_VIEW_MODE,
  VIEW_MODE_FROM_PHASE,
  parsePhaseFromSearch,
  recallSubModeFor,
  serializePhaseToSearch,
} from '../components/environment/canvas/phaseMapping';
import { InvestigationNode } from '../components/environment/projection/InvestigationNode';
import { collectProjectedInvestigationIds } from '../components/environment/projection/projectedInvestigationIds';
import { ForgeNodeList } from '../components/environment/projection/ForgeNode';
import { CompositionPickerOverlay } from '../components/environment/CompositionPickerOverlay';
import { useGlobalTheatre } from '../hooks/useGlobalTheatre';
import { useInvestigationList } from '../hooks/useInvestigation';
import { useQuery } from '@tanstack/react-query';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { Play, Check, XCircle, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { apiClient } from '../api/client';
import { theatreApi } from '../api/theatres';


import { D3GraphCanvas, type D3GraphNode, type D3GraphEdge } from '../components/environment/graph/D3GraphCanvas';
import { isSeededNode } from '../components/environment/graph/freshness';
import { type GraphNode, type GraphResponse } from '../components/environment/graph/useGraphData';
import { DetailCard } from '../components/environment/DetailCard';
import { LineageTimelineStrip } from '../components/environment/LineageTimelineStrip';

// ── Types ──

type ViewMode = 'build' | 'monitor' | 'lineage' | 'runs' | 'memory' | 'review';


const MODES: Array<{ id: ViewMode; label: string; key: string }> = [
  { id: 'build', label: 'Build', key: '1' },
  { id: 'monitor', label: 'Monitor', key: '2' },
  { id: 'lineage', label: 'Lineage', key: '3' },
  { id: 'runs', label: 'Runs', key: '4' },
  { id: 'memory', label: 'Memory', key: '5' },
  { id: 'review', label: 'Review', key: '6' },
];

// ── Builder Studio Review (Cycle 076 Sprint 1) ──

interface Proposal {
  id: string;
  theatre_id: string;
  attestation_id: string;
  domain: string;
  claim_category: string;
  title: string;
  summary: string | null;
  evidence_chain: Record<string, unknown>;
  status: string;
  created_at: string | null;
  reviewed_at: string | null;
  reviewed_by: string | null;
}

function BuilderStudioReview({ theatreId }: { theatreId: string }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['knowledge-proposals', theatreId],
    queryFn: async () => {
      const { data } = await apiClient.get<{ proposals: Proposal[]; count: number }>(
        `/api/v1/knowledge/proposals?status=PENDING&theatre_id=${theatreId}`,
      );
      return data;
    },
    enabled: !!theatreId,
    staleTime: 10_000,
  });

  const approveMutation = useMutation({
    mutationFn: async ({ proposalId, title, summary }: { proposalId: string; title?: string; summary?: string }) => {
      const { data } = await apiClient.post(`/api/v1/knowledge/proposals/${proposalId}/approve`, {
        reviewed_by: 'operator',
        title,
        summary,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-proposals'] });
      queryClient.invalidateQueries({ queryKey: ['environment-graph-d3'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (proposalId: string) => {
      const { data } = await apiClient.post(`/api/v1/knowledge/proposals/${proposalId}/reject`, {
        reviewed_by: 'operator',
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-proposals'] });
    },
  });

  const proposals = data?.proposals ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[11px] font-mono text-[var(--e-text-muted)] animate-pulse">Loading proposals...</div>
      </div>
    );
  }

  if (proposals.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center space-y-2">
          <div className="text-[12px] text-[var(--e-text-secondary)]">No pending proposals</div>
          <div className="text-[10px] text-[var(--e-text-muted)]">
            Knowledge Node proposals appear here when a Theatre achieves CERTIFIED status.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-6 py-4">
      <div className="max-w-2xl mx-auto space-y-3">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[8px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]/60">
            Builder Studio — Review Queue
          </div>
          <div className="text-[9px] font-mono text-[var(--e-text-muted)]">
            {proposals.length} pending
          </div>
        </div>

        {proposals.map((p) => (
          <div key={p.id} className="rounded-lg overflow-hidden"
            style={{ background: 'var(--e-env-bg-card)', border: '1px solid var(--e-env-border)' }}>
            {/* Accent bar */}
            <div className="h-0.5" style={{ background: 'oklch(0.510 0.168 278)' }} />

            <div className="px-4 py-3 space-y-2">
              {/* Header */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[12px] font-medium text-[var(--e-text-primary)]">{p.title}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[8px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ background: 'oklch(0.510 0.168 278 / 0.15)', color: 'oklch(0.510 0.168 278)' }}>
                      {p.domain}
                    </span>
                    <span className="text-[9px] font-mono text-[var(--e-text-muted)]">{p.claim_category}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <Button size="sm" variant="outline" className="h-6 text-[9px] px-2 text-[#ef4444] border-[#ef4444]/30 hover:bg-[#ef4444]/10"
                    disabled={rejectMutation.isPending}
                    onClick={() => rejectMutation.mutate(p.id)}>
                    <XCircle className="w-3 h-3 mr-1" />Reject
                  </Button>
                  <Button size="sm" className="h-6 text-[9px] px-2 bg-[#22c55e] hover:bg-[#16a34a] text-white"
                    disabled={approveMutation.isPending}
                    onClick={() => approveMutation.mutate({ proposalId: p.id })}>
                    <Check className="w-3 h-3 mr-1" />Approve
                  </Button>
                </div>
              </div>

              {/* Summary */}
              {p.summary && (
                <div className="text-[10px] text-[var(--e-text-muted)] line-clamp-3">{p.summary}</div>
              )}

              {/* Evidence chain summary */}
              <div className="flex items-center gap-3 text-[9px] font-mono text-[var(--e-text-muted)]">
                <span>Attestation: {p.attestation_id.slice(0, 12)}...</span>
                {p.evidence_chain.composite_score != null && (
                  <span>Score: {Number(p.evidence_chain.composite_score).toFixed(2)}</span>
                )}
                {p.evidence_chain.replay_verified === true && (
                  <span className="text-[#22c55e]">Replay verified</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Lineage View (vertical spine — Sprint 2) ──

function LineageView({
  nodes,
  edges,
  onNodeClick,
}: {
  nodes: D3GraphNode[];
  edges: D3GraphEdge[];
  onNodeClick?: (nodeId: string) => void;
}) {
  // Sort nodes chronologically — investigations and runs interleaved
  // Group by type for the spine
  const timelineNodes = useMemo(() => {
    const relevant = nodes.filter(n =>
      ['investigation', 'hub', 'run', 'attestation', 'knowledge'].includes(n.type)
    );
    // Stable sort: hub first, then investigations, runs, attestations, knowledge
    const order: Record<string, number> = { hub: 0, investigation: 1, run: 2, attestation: 3, knowledge: 4, construct: 5 };
    return [...relevant].sort((a, b) => (order[a.type] ?? 99) - (order[b.type] ?? 99));
  }, [nodes]);

  // Build typed edge lookup: source → [{ target, edgeType }]
  const edgeMap = useMemo(() => {
    const map = new Map<string, Array<{ targetId: string; edgeType: string }>>();
    edges.forEach(e => {
      const src = typeof e.source === 'string' ? e.source : e.source.id;
      const tgt = typeof e.target === 'string' ? e.target : e.target.id;
      if (!map.has(src)) map.set(src, []);
      map.get(src)!.push({ targetId: tgt, edgeType: e.type });
    });
    return map;
  }, [edges]);

  const NODE_COLOURS: Record<string, string> = {
    hub: 'oklch(0.530 0.205 295)',
    investigation: 'oklch(0.548 0.172 198)',
    run: 'oklch(0.568 0.160 152)',
    attestation: 'oklch(0.688 0.165 72)',
    knowledge: 'oklch(0.510 0.168 278)',
    construct: 'oklch(0.530 0.205 295)',
  };

  if (timelineNodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-[11px] text-[var(--e-text-muted)]">No lineage data</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-8 py-6">
      <div className="max-w-2xl mx-auto relative">
        {/* Vertical spine line */}
        <div className="absolute left-5 top-0 bottom-0 w-px" style={{ background: 'var(--e-env-border)' }} />

        {timelineNodes.map((node) => {
          const colour = NODE_COLOURS[node.type] ?? '#64748b';
          const outgoing = edgeMap.get(node.id) ?? [];
          // Group by edge type for display
          const byType = new Map<string, string[]>();
          outgoing.forEach(({ targetId, edgeType }) => {
            const label = nodes.find(n => n.id === targetId)?.label;
            if (!label) return;
            if (!byType.has(edgeType)) byType.set(edgeType, []);
            byType.get(edgeType)!.push(label);
          });

          const EDGE_COLOURS: Record<string, string> = {
            FEEDS: 'oklch(0.668 0.162 175)', SPAWNED_BY: 'oklch(0.548 0.172 198)',
            PROMOTES: 'oklch(0.440 0.110 258)', ATTESTS: 'oklch(0.530 0.205 295)',
            TEMPLATES: 'oklch(0.568 0.160 152)', COMPILED_FROM: 'oklch(0.440 0.110 258)',
            CALIBRATED_WITH: 'oklch(0.658 0.148 195)', INFORMS: 'oklch(0.510 0.168 278)',
          };

          return (
            <div key={node.id} className="relative pl-12 pb-4 group">
              {/* Node dot on spine */}
              <div className="absolute left-3.5 top-1.5 w-3 h-3 rounded-full border-2"
                style={{ background: colour + '33', borderColor: colour }} />

              {/* Node card */}
              <button
                onClick={() => onNodeClick?.(node.id)}
                className="w-full text-left px-3 py-2 rounded-lg transition-colors hover:bg-[var(--e-bg-hover)]"
              >
                <div className="flex items-center gap-2">
                  <span className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{ background: colour + '22', color: colour }}>
                    {node.type === 'hub' ? 'theatre' : node.type}
                  </span>
                  <span className="text-[11px] font-medium text-[var(--e-text-primary)]">{node.label}</span>
                  {node.status && node.status !== 'idle' && (
                    <span className="text-[9px] font-mono text-[var(--e-text-muted)]">{node.status}</span>
                  )}
                </div>
                {node.sublabel && (
                  <div className="text-[9px] text-[var(--e-text-muted)] mt-0.5">{node.sublabel}</div>
                )}
                {/* Typed edge connections */}
                {Array.from(byType.entries()).map(([edgeType, labels]) => (
                  <div key={edgeType} className="flex items-center gap-1 mt-0.5">
                    <span className="text-[8px] font-mono" style={{ color: EDGE_COLOURS[edgeType] ?? '#64748b' }}>
                      {edgeType} →
                    </span>
                    <span className="text-[8px] font-mono text-[var(--e-text-muted)]">{labels.join(', ')}</span>
                  </div>
                ))}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Runs List ──

function RunsList({ theatreId }: { theatreId: string }) {
  const { data: runs } = useQuery({
    queryKey: ['environment-runs', theatreId],
    queryFn: async () => { const { data } = await apiClient.get(`/api/v1/environment/runs?theatre_id=${theatreId}&limit=50`); return data?.runs ?? []; },
    enabled: !!theatreId,
  });

  return (
    <div className="flex-1 overflow-y-auto px-6 py-4">
      <div className="text-[8px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]/60 mb-3">Run History</div>
      {(!runs || runs.length === 0) ? (
        <div className="text-[11px] text-[var(--e-text-muted)] text-center py-8">No runs recorded yet.</div>
      ) : (
        <div className="space-y-1">
          {(runs as any[]).map((run: any) => (
            <div key={run.id} className="flex items-center justify-between px-3 py-2 rounded text-[10px] font-mono hover:bg-[var(--e-bg-hover)] cursor-pointer">
              <div className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full" style={{
                  background: run.status === 'completed' ? '#22c55e' : run.status === 'failed' ? '#ef4444' : run.status === 'running' ? '#8b5cf6' : '#6b7280',
                }} />
                <span className="text-[var(--e-text-secondary)]">Run #{run.run_number ?? run.id?.slice(-6)}</span>
              </div>
              <span className="text-[var(--e-text-muted)]">{run.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Backend → D3 type mapping ──

/** Map backend node_type to D3 canvas shape type */
function backendNodeTypeToD3(nodeType: string): string {
  switch (nodeType) {
    case 'theatre': return 'hub';
    case 'template': return 'construct';
    default: return nodeType; // investigation, run, attestation, knowledge pass through
  }
}

/** Map backend edge_type to D3 canvas edge type (lowercase for EDGE_TYPES lookup) */
/** Pass backend edge types through directly — D3 canvas has all registry types */
function backendEdgeTypeToD3(edgeType: string): string {
  return edgeType;
}

/** Map backend status metadata to D3 status string */
function resolveNodeStatus(node: GraphResponse['nodes'][0]): string {
  const status = (node.metadata?.status as string) ?? 'idle';
  if (node.node_type === 'knowledge' && node.verification_state === 'VALIDATED') return 'verified';
  if (node.node_type === 'run' && status === 'COMPLETED') return 'completed';
  if (node.node_type === 'run' && status === 'RUNNING') return 'running';
  if (node.node_type === 'run' && status === 'FAILED') return 'failed';
  return status.toLowerCase();
}

interface TransformedGraph {
  nodes: D3GraphNode[];
  edges: D3GraphEdge[];
  /** Full backend nodes keyed by node_id — source of truth for inspector */
  nodeIndex: Map<string, GraphResponse['nodes'][0]>;
}

/** Transform backend GraphResponse into D3-ready arrays + lookup index */
function transformGraphResponse(data: GraphResponse): TransformedGraph {
  const incomingNodes = data?.nodes ?? [];
  const incomingEdges = data?.edges ?? [];
  const nodeIds = new Set(incomingNodes.map(n => n.node_id));

  const nodeIndex = new Map<string, GraphResponse['nodes'][0]>();
  const nodes: D3GraphNode[] = incomingNodes.map(n => {
    nodeIndex.set(n.node_id, n);
    return {
      id: n.node_id,
      type: backendNodeTypeToD3(n.node_type),
      label: n.title,
      sublabel: n.subtitle ?? undefined,
      status: resolveNodeStatus(n),
      knowledgeState: n.verification_state ?? undefined,
      signalCount: n.evidence_bundle_ref?.evidence_count ?? undefined,
      // GAP-3 Tier 1 (sprint-236): contesting-evidence count from the real
      // evidence bundle — absent/0 renders nothing (honest handling).
      counterSignalCount: n.evidence_bundle_ref?.counter_signal_count ?? undefined,
    };
  });

  const edges: D3GraphEdge[] = incomingEdges
    .filter(e => nodeIds.has(e.source_node_id) && nodeIds.has(e.target_node_id))
    .map(e => ({
      source: e.source_node_id,
      target: e.target_node_id,
      type: backendEdgeTypeToD3(e.edge_type),
      label: e.label ?? undefined,
    }));

  return { nodes, edges, nodeIndex };
}

// ── Environment graph hook (fetches from backend, transforms for D3) ──

/** True for HTTP statuses that will not heal on retry (sprint-236 §4.1.4
 * read-surface verification: `/knowledge-graph` is auth-GATED backend-side —
 * an unauthenticated ambient viewer gets a deterministic 401). */
function isDeterministicHttpError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 401 || status === 403 || status === 404;
}

function useEnvironmentGraph(theatreId: string | null) {
  return useQuery({
    queryKey: ['environment-graph-d3', theatreId],
    queryFn: async () => {
      const { data } = await apiClient.get<GraphResponse>(
        `/api/v1/environment/theatres/${theatreId}/knowledge-graph`,
      );
      return transformGraphResponse(data);
    },
    enabled: !!theatreId,
    staleTime: 30_000,
    // Deterministic 401/403/404 → fail fast into the honest-empty state
    // (same bug class as the forge-rail stuck-load); transient errors keep
    // the default backoff.
    retry: (failureCount, error) =>
      isDeterministicHttpError(error) ? false : failureCount < 3,
  });
}

// ── Main Page ──

/** Extract a human-readable detail from an axios error (FR-B2 run feedback). */
function runErrorMessage(error: unknown): string {
  const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
  return detail || 'Run failed. Please try again.';
}

export function EnvironmentGraphPage() {
  const { theatreId: routeTheatreId } = useParams<{ theatreId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Cycle-102 sprint-2 Task 2.4 — phase derived from URL `?phase=`,
  // legacy view mode kept in lockstep so the existing canvas-branching
  // (`viewMode === 'lineage'`, etc.) continues to work without
  // refactoring D3GraphCanvas (Task 2.2 deferred).
  const { phase: urlPhase, sub: urlSub } = parsePhaseFromSearch(location.search);
  const initialViewMode: ViewMode = urlSub ?? VIEW_MODE_FROM_PHASE[urlPhase];
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode);

  const activePhase: PhaseId = PHASE_FROM_VIEW_MODE[viewMode];

  const setPhase = useCallback(
    (next: PhaseId) => {
      const nextView = VIEW_MODE_FROM_PHASE[next];
      setViewMode(nextView);
      const params = serializePhaseToSearch(location.search, { phase: next });
      navigate({ pathname: location.pathname, search: `?${params.toString()}` }, { replace: true });
    },
    [location.pathname, location.search, navigate],
  );

  // When viewMode changes (e.g. via keyboard shortcut), keep URL phase in sync.
  useEffect(() => {
    const expectedPhase = PHASE_FROM_VIEW_MODE[viewMode];
    const sub = recallSubModeFor(viewMode);
    const next = serializePhaseToSearch(location.search, {
      phase: expectedPhase,
      sub,
    });
    if (next.toString() !== new URLSearchParams(location.search.slice(1)).toString()) {
      navigate(
        { pathname: location.pathname, search: `?${next.toString()}` },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // ── Ambient Global Intelligence (sprint-236, SDD §4.1 REVISION 2) ──
  // Unscoped /environment = the ambient Global Intelligence surface: resolve
  // the global container via GET /environment/global-theatre and render its
  // knowledge graph as the ambient canvas. /theatres/:id/build (scoped)
  // behaviour is unchanged — the route param wins.
  const isAmbient = !routeTheatreId;
  const {
    data: globalTheatre,
    isError: globalTheatreError,
    isFetched: globalTheatreResolved,
  } = useGlobalTheatre(isAmbient);

  const activeTheatreId =
    routeTheatreId ?? (isAmbient ? globalTheatre?.theatre_id ?? null : null);

  // ── Graph data from backend ──
  const {
    data: graphData,
    isError: graphIsError,
    error: graphError,
    refetch: refetchGraph,
  } = useEnvironmentGraph(activeTheatreId ?? null);

  const rawGraphNodes = graphData?.nodes ?? [];
  const graphEdges = graphData?.edges ?? [];

  // Ontology invariant (SDD §6.3.1, operator-locked): the global container is
  // a permanent Dossier — NEVER labelled "Theatre" in operator-facing strings.
  // The backend hub node carries the theatre's construct_id/state; on the
  // ambient surface, relabel the hub to "Global Intelligence".
  const graphNodes = useMemo(() => {
    if (!isAmbient) return rawGraphNodes;
    return rawGraphNodes.map((n) =>
      n.type === 'hub'
        ? { ...n, label: 'Global Intelligence', sublabel: 'ambient surface' }
        : n,
    );
  }, [isAmbient, rawGraphNodes]);

  // EARNED-CONTENT INVARIANT (SDD §6.4.1): render exactly what the API
  // returns — never fixtures. "Earned" nodes exclude the container hub and
  // template scaffolding the backend always emits.
  //
  // Sprint-241 rule (c) extension: SEEDED knowledge nodes (attestation_id
  // sentinel "seed:manual" — graph/freshness.ts) are not-yet-earned. The
  // predicate becomes: earned = nodes that are NOT hub/construct scaffolding
  // AND NOT seeded knowledge — so a graph holding ONLY seeded baselines still
  // shows the honest-empty ambient prompt. Seeded nodes still RENDER on the
  // canvas (never hidden — rule a); they just don't count as earned. Any
  // other node type (run, attestation, investigation, graduated knowledge)
  // counts as earned, exactly as before.
  const nodeIndexForEarned = graphData?.nodeIndex;
  const earnedNodeCount = useMemo(
    () =>
      rawGraphNodes.filter((n) => {
        if (n.type === 'hub' || n.type === 'construct') return false;
        if (n.type === 'knowledge' && isSeededNode(nodeIndexForEarned?.get(n.id))) return false;
        return true;
      }).length,
    [rawGraphNodes, nodeIndexForEarned],
  );

  // Honest-empty ambient (frame 3): global container unset (404) / errored,
  // the graph resolved with zero earned nodes, OR the graph query failed
  // DETERMINISTICALLY (401/403/404 — e.g. an unauthenticated viewer; the
  // knowledge-graph read is auth-gated backend-side; degrade honestly
  // instead of a blank/stuck canvas — sprint-236 §4.1.4 verification).
  // Transient failures (500/network) are NOT empty — they route to the
  // ambient ERROR state below (sprint-236 review BLOCKING-2): the
  // "No verified intelligence yet" copy must never claim emptiness when
  // the graph simply failed to load.
  const showAmbientEmpty =
    isAmbient &&
    ((globalTheatreResolved && (globalTheatre === null || globalTheatreError)) ||
      (!!graphData && earnedNodeCount === 0) ||
      (graphIsError && isDeterministicHttpError(graphError)));

  // Ambient + transient (non-deterministic) graph error → distinct error
  // state with Retry — never the honest-empty copy.
  const showAmbientError =
    isAmbient && graphIsError && !isDeterministicHttpError(graphError);

  // ── Composition picker overlay (sprint-236 Task 236.4) ──
  const [pickerOpen, setPickerOpen] = useState(false);

  // ── FR-B2 (cycle-115): Run button wiring ──
  // The /run route requires state === 'COMMITTED' (theatre_routes.py:265) and
  // rejects market theatres with 400 (:273). We fetch the active theatre's state
  // to gate the button proactively on COMMITTED, and reflect the residual
  // market-400 (which the frontend type can't predict) on error.
  const { data: activeTheatre } = useQuery({
    queryKey: ['theatre-detail', activeTheatreId],
    queryFn: () => theatreApi.getTheatre(activeTheatreId as string),
    enabled: !!activeTheatreId,
    staleTime: 10_000,
  });

  const runMutation = useMutation({
    mutationFn: (theatreId: string) => theatreApi.runTheatre(theatreId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['theatre-detail', activeTheatreId] });
      queryClient.invalidateQueries({ queryKey: ['environment-graph-d3'] });
    },
  });

  // Run targets scoped compositions only — the ambient Global Intelligence
  // surface is a recall surface, not an executable composition (§6.3.1).
  const canRunTheatre = !!routeTheatreId && activeTheatre?.state === 'COMMITTED';
  const runButtonTitle = !routeTheatreId
    ? 'Open a composition to run'
    : runMutation.isError
      ? runErrorMessage(runMutation.error)
      : activeTheatre?.state !== 'COMMITTED'
        ? `Only COMMITTED theatres can be run (this theatre is ${activeTheatre?.state ?? 'loading'})`
        : 'Run this theatre';

  // ── Derived overlay data (FR-E-1) ──
  // Computed from the existing graph query only (no new fetch); provenance
  // hashes come from real backend metadata and are never fabricated (NFR-4).
  const overlayData: PhaseOverlayData = useMemo(() => {
    const nodes = graphNodes;
    const nodeIndex = graphData?.nodeIndex;
    const typeColor: Record<string, string> = {
      hub: 'oklch(0.530 0.205 295)',
      construct: 'oklch(0.530 0.205 295)',
      investigation: 'oklch(0.548 0.172 198)',
      knowledge: 'oklch(0.510 0.168 278)',
      run: 'oklch(0.568 0.160 152)',
      attestation: 'oklch(0.688 0.165 72)',
    };

    const byTypeMap = new Map<string, number>();
    nodes.forEach((n) => byTypeMap.set(n.type, (byTypeMap.get(n.type) ?? 0) + 1));
    const byType = [...byTypeMap.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([type, count]) => ({ type, count, color: typeColor[type] ?? 'oklch(0.480 0.007 265)' }));

    const runNodes = nodes.filter((n) => n.type === 'run');
    const live = runNodes.filter((n) => n.status === 'running' || n.status === 'active').length;
    const runs = runNodes.map((n) => ({ id: n.id, label: n.label, status: n.status ?? 'idle' }));

    const attestationNodes = nodes.filter((n) => n.type === 'attestation');
    const provenance = attestationNodes.map((n) => {
      const md = nodeIndex?.get(n.id)?.metadata ?? {};
      const raw =
        (md.hash as string) ??
        (md.content_hash as string) ??
        (md.attestation_hash as string) ??
        (md.receipt_hash as string) ??
        undefined;
      return { id: n.id, label: n.label, hash: typeof raw === 'string' ? raw : undefined };
    });

    const knowledgeNodes = nodes.filter((n) => n.type === 'knowledge');
    const validated = knowledgeNodes.filter((n) => n.knowledgeState === 'VALIDATED').length;
    const provisional = knowledgeNodes.filter((n) => n.knowledgeState === 'PROVISIONAL').length;
    const proveRungs: PhaseOverlayData['proveRungs'] = [
      { label: 'Validated', count: validated, tone: 'verified' },
      { label: 'Provisional', count: provisional, tone: 'provisional' },
      { label: 'Attested', count: attestationNodes.length, tone: attestationNodes.length > 0 ? 'verified' : 'pending' },
    ];

    const lineage = nodes
      .filter((n) => n.type === 'knowledge' || n.type === 'attestation')
      .map((n) => ({ id: n.id, label: n.label }));

    const memoryTiers = [
      { label: 'Knowledge', color: 'oklch(0.510 0.168 278)', count: knowledgeNodes.length },
      { label: 'Investigations', color: 'oklch(0.548 0.172 198)', count: nodes.filter((n) => n.type === 'investigation').length },
      { label: 'Theatres & Runs', color: 'oklch(0.530 0.205 295)', count: nodes.filter((n) => n.type === 'hub' || n.type === 'run').length },
    ];

    return {
      compose: { nodeCount: nodes.length, edgeCount: graphEdges.length, byType },
      live,
      runs,
      provenance,
      proveRungs,
      lineage,
      memoryTiers,
    };
  }, [graphNodes, graphEdges, graphData]);
  const nodeIndex = graphData?.nodeIndex ?? new Map();

  // ── Selected node data (from D3 node click) ──
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);

  const handleNodeSelect = useCallback((node: D3GraphNode | null) => {
    setSelectedNodeId(node?.id ?? null);
  }, []);

  // Double-click deep-link: investigation → Workspace, theatre → Theatre detail
  const handleNodeDoubleClick = useCallback((node: D3GraphNode) => {
    if (node.type === 'investigation') {
      const rawId = node.id.includes(':') ? node.id.split(':').slice(1).join(':') : node.id;
      navigate(`/workspace/investigations/${rawId}`);
    } else if (node.type === 'hub') {
      const rawId = node.id.includes(':') ? node.id.split(':').slice(1).join(':') : node.id;
      navigate(`/theatres/${rawId}`);
    }
  }, [navigate]);

  // ── Remove node from composition ──
  const handleDeleteNode = useCallback(async (nodeId: string) => {
    setSelectedNodeId(null);

    // Composition mutations are scoped-route-only; the ambient Global
    // Intelligence canvas is read-only (sprint-236).
    if (!routeTheatreId || !activeTheatreId) return;

    try {
      // Check if it's an investigation node → use remove-from-environment
      if (nodeId.startsWith('investigation:')) {
        const invId = nodeId.split(':').slice(1).join(':');
        await apiClient.post(`/api/v1/investigations/${invId}/remove-from-environment`, {
          theatre_id: activeTheatreId,
        });
      } else {
        // Module/source/template → remove from build_state_json
        const { data: current } = await apiClient.get<{ build_state: any }>(
          `/api/v1/theatres/${activeTheatreId}/build-state`,
        );
        const buildState = current?.build_state ?? { nodes: {}, connections: [] };

        // Find the node key — it's the part after "type:"
        const nodeKey = nodeId.includes(':') ? nodeId.split(':').slice(1).join(':') : nodeId;
        if (buildState.nodes[nodeKey]) {
          delete buildState.nodes[nodeKey];
          // Also remove connections referencing this node (supports both field formats)
          buildState.connections = (buildState.connections || []).filter(
            (c: any) => {
              const src = c.fromNodeId ?? c.source;
              const tgt = c.toNodeId ?? c.target;
              return src !== nodeKey && tgt !== nodeKey;
            },
          );
          await apiClient.put(`/api/v1/theatres/${activeTheatreId}/build-state`, buildState);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['environment-graph-d3'] });
    } catch (err) {
      console.error('[Environment] Failed to remove node:', err);
    }
  }, [routeTheatreId, activeTheatreId, queryClient]);

  // Look up the full typed backend node for the inspector — no lossy reverse-mapping
  const selectedNode: GraphNode | null = useMemo(() => {
    if (!selectedNodeId) return null;
    const backendNode = nodeIndex.get(selectedNodeId);
    if (!backendNode) return null;
    return backendNode as unknown as GraphNode;
  }, [selectedNodeId, nodeIndex]);

  // ── Keyboard shortcuts ──
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.metaKey || e.ctrlKey) return;
      // While the full-screen DetailCard (modal dialog) is open, suppress
      // background page hotkeys (review DISS-001): destructive Delete/Backspace
      // and the 1-6 mode-switch keys must not fire behind the dialog. The card
      // owns its own controls — ESC to close (DetailCard) and the explicit
      // "Remove from composition" button (which calls handleDeleteNode).
      if (selectedNodeId) return;
      // Mode switching (only when no node is selected / no card open)
      const entry = MODES.find((m) => m.key === e.key);
      if (entry) { e.preventDefault(); setViewMode(entry.id); }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNodeId]);


  return (
    <div className="flex flex-col overflow-hidden" style={{ background: 'var(--e-env-bg-app)', height: 'calc(100dvh - 3.5rem)' }}>
      {/* Cycle-102 sprint-2 Task 2.4 — PhaseTabs replaces the legacy
          6-button view-mode bar. Legacy view modes are mapped onto the
          4 phases per SDD R-SDD-9; `lineage` and `review` remain
          accessible as RECALL sub-modes via a small chip below. */}
      <PhaseTabs
        activePhase={activePhase}
        onPhaseChange={setPhase}
        canvasId="environment-canvas-d3"
      />
      {/* Context bar */}
      <header className="h-10 flex items-center gap-3 px-4 flex-shrink-0 z-40"
        style={{ background: 'var(--e-env-bg-panel)', borderBottom: '1px solid var(--e-env-border-subtle)' }}>
        {/* Session/context strip (sprint-236): ambient = Global Intelligence
            row; scoped = `‹ CODE · name ×` overlay chip dismissing back to
            the ambient surface (Workspace-globe pattern). */}
        <div className="flex items-center gap-2 text-[10px] font-mono" data-testid="domain-context-strip">
          {isAmbient ? (
            <span
              className="uppercase tracking-widest text-[var(--e-text-secondary)]"
              data-testid="ambient-session-row"
            >
              ◈ GLOBAL INTELLIGENCE · ambient surface
            </span>
          ) : (
            <span
              className="flex items-center gap-1.5 px-2 py-0.5 rounded border border-[var(--e-border-primary)] bg-[var(--e-bg-elevated)]"
              data-testid="scoped-overlay-chip"
            >
              <span className="text-[var(--e-text-muted)]">‹</span>
              <span className="uppercase tracking-wider text-[var(--e-text-primary)]">
                {activeTheatre?.construct_id ?? routeTheatreId?.slice(0, 8)}
              </span>
              <span className="text-[var(--e-text-muted)]">·</span>
              <span className="text-[var(--e-text-secondary)]">
                {routeTheatreId?.slice(0, 8)}
              </span>
              <button
                type="button"
                aria-label="Close composition and return to Global Intelligence"
                data-testid="scoped-overlay-dismiss"
                onClick={() => navigate('/environment')}
                className="ml-0.5 text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)]"
              >
                ×
              </button>
            </span>
          )}
          <span className="text-[var(--e-text-muted)]">·</span>
          <span className="text-[var(--e-text-secondary)]">{overlayData.compose.nodeCount} nodes</span>
        </div>

        {/* OPEN COMPOSITION › — overlay picker over the dimmed ambient
            canvas (frames 4/5). Ambient surface only. */}
        {isAmbient && (
          <button
            type="button"
            data-testid="open-composition"
            onClick={() => setPickerOpen(true)}
            className="px-2 py-0.5 rounded border border-[var(--e-border-primary)] text-[9px] font-mono uppercase tracking-widest text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)] hover:bg-[var(--e-bg-hover)] transition-colors"
          >
            OPEN COMPOSITION ›
          </button>
        )}

        {/* Sub-mode selector — only visible on RECALL phase (memory/lineage/review). */}
        {activePhase === 'RECALL' && (
          <nav
            aria-label="Recall sub-mode"
            className="flex items-center gap-0.5"
          >
            {(['memory', 'lineage', 'review'] as const).map((sub) => (
              <button
                key={sub}
                type="button"
                onClick={() => setViewMode(sub)}
                aria-pressed={viewMode === sub}
                className={cn(
                  'px-2 py-1 rounded text-[10px] font-mono transition-colors capitalize',
                  viewMode === sub
                    ? 'text-[var(--e-text-primary)] bg-[var(--e-bg-hover)]'
                    : 'text-[var(--e-text-muted)] hover:text-[var(--e-text-secondary)]',
                )}
              >
                {sub}
              </button>
            ))}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-3">
          {/* Live status counter (FR-E-2) — running/active node count. */}
          <div
            className="flex items-center gap-1.5 text-[10px] font-mono"
            data-testid="live-status-counter"
            aria-live="polite"
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: overlayData.live > 0 ? 'oklch(0.568 0.160 152)' : 'oklch(0.300 0.007 265)' }}
            />
            <span style={{ color: overlayData.live > 0 ? 'oklch(0.720 0.140 152)' : 'oklch(0.480 0.007 265)' }}>
              {overlayData.live} live
            </span>
          </div>

          {/* Import action (FR-E-2 / FR-H-1) — projects a Workspace investigation
              into Environment; wired to a real navigation, not a dead button. */}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate('/workspace')}
            title="Import a projected investigation from Workspace"
            data-testid="env-import-action"
            className="h-7 text-[10px] text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)]"
          >
            <Download className="w-3 h-3 mr-1" />Import
          </Button>

          <Button
            size="sm"
            className="h-7 text-[10px] bg-[var(--e-purple-500)] hover:bg-[var(--e-purple-600)] disabled:opacity-40 disabled:cursor-not-allowed"
            disabled={!canRunTheatre || runMutation.isPending}
            onClick={() => activeTheatreId && runMutation.mutate(activeTheatreId)}
            title={runButtonTitle}
            data-testid="env-run-action"
            aria-live="polite"
          >
            <Play className="w-3 h-3 mr-1" />{runMutation.isPending ? 'Running…' : 'Run'}
          </Button>
        </div>
      </header>

      {/* Main content — dot-field gradient depth background (FR-E-5):
          a soft top-down depth gradient layered under a fine dot grid,
          replacing the flat oklch fill. No WebGL (NFR-2). */}
      <div
        className="flex-1 relative overflow-hidden"
        style={{
          backgroundColor: 'var(--e-env-bg-app)',
          backgroundImage:
            'radial-gradient(120% 80% at 50% 0%, var(--e-env-bg-card) 0%, var(--e-env-bg-app) 58%), radial-gradient(color-mix(in oklch, var(--e-env-border) 30%, transparent) 1px, transparent 1.4px)',
          backgroundSize: '100% 100%, 22px 22px',
          backgroundPosition: '0 0, 0 0',
        }}
      >
        {showAmbientError ? (
          /* Transient graph failure (sprint-236 review BLOCKING-2): a
             500/network error is NOT an empty graph — render an honest
             error state with Retry; the "No verified intelligence yet"
             empty copy must never appear here. */
          <div
            className="absolute inset-0 flex items-center justify-center"
            data-testid="ambient-error-state"
          >
            <div className="text-center space-y-2">
              <div className="mx-auto w-10 h-10 rounded-full border border-dashed border-[var(--e-border-primary)]" />
              <div className="text-[12px] text-[var(--e-text-secondary)]">
                Unable to load Global Intelligence
              </div>
              <div className="text-[10px] font-mono text-[var(--e-text-muted)] max-w-[280px] mx-auto">
                The graph could not be loaded. This is a connection or server
                problem — not an empty graph.
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => refetchGraph()}
                data-testid="ambient-error-retry"
                className="h-7 text-[10px] text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)]"
              >
                Retry
              </Button>
            </div>
          </div>
        ) : showAmbientEmpty ? (
          /* Honest-empty ambient (frame 3, SDD §6.4.1): global container
             unset or zero earned nodes — no fixtures, no fake graph. */
          <div
            className="absolute inset-0 flex items-center justify-center"
            data-testid="ambient-empty-state"
          >
            <div className="text-center space-y-2">
              <div className="mx-auto w-10 h-10 rounded-full border border-dashed border-[var(--e-border-primary)]" />
              <div className="text-[12px] text-[var(--e-text-secondary)]">
                No verified intelligence yet
              </div>
              <div className="text-[10px] font-mono text-[var(--e-text-muted)]">
                run a Theatre to begin
              </div>
            </div>
          </div>
        ) : viewMode === 'lineage' ? (
          <LineageView nodes={graphNodes} edges={graphEdges} onNodeClick={(id) => setSelectedNodeId(id)} />
        ) : viewMode === 'runs' ? (
          <RunsList theatreId={activeTheatreId ?? ''} />
        ) : viewMode === 'review' ? (
          <BuilderStudioReview theatreId={activeTheatreId ?? ''} />
        ) : (
          <D3GraphCanvas
            nodes={graphNodes} edges={graphEdges}
            onNodeSelect={handleNodeSelect} onNodeDoubleClick={handleNodeDoubleClick}
            layout={viewMode === 'memory' ? 'memory' : 'force'}
            onPlaceModule={async (moduleKind) => {
              // Scoped-route-only — ambient Global Intelligence is read-only.
              if (!routeTheatreId || !activeTheatreId) return;
              try {
                const { data: current } = await apiClient.get<{ build_state: any }>(
                  `/api/v1/theatres/${activeTheatreId}/build-state`,
                );
                const buildState = current?.build_state ?? { nodes: {}, connections: [] };
                const nodeKey = `${moduleKind}_${Date.now()}`;
                buildState.nodes[nodeKey] = {
                  label: moduleKind.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
                  module_kind: moduleKind,
                  node_type: 'module',
                };
                await apiClient.put(`/api/v1/theatres/${activeTheatreId}/build-state`, buildState);
                queryClient.invalidateQueries({ queryKey: ['environment-graph-d3'] });
              } catch (err) {
                console.error('[Environment] Failed to place module:', err);
              }
            }}
          />
        )}

        {/* Phase-switched floating overlay over the canvas region (FR-E-1). */}
        {graphNodes.length > 0 && (
          <PhaseOverlay activePhase={activePhase} data={overlayData} />
        )}

        {/* Full-screen 5-tab node detail (FR-E-4) — replaces the 280px
            FloatingInspector. Driven by the selected node + the D3 graph;
            the lane-based lineage MiniMap lives in its right sidebar. */}
        <DetailCard
          node={selectedNode}
          nodeId={selectedNodeId}
          nodes={graphNodes}
          edges={graphEdges}
          theatreId={activeTheatreId}
          onClose={() => setSelectedNodeId(null)}
          onNavigate={navigate}
          onDelete={handleDeleteNode}
          onSelectNode={(id) => setSelectedNodeId(id)}
        />

        {/* Cycle-102 sprint-3 — Workspace ↔ Environment projection rail.
            Active investigations + FORGE patterns rendered as small
            visual nodes (sprint-2 NodeShapeRoot). Non-invasive: this
            panel sits beside the canvas without touching the d3-force
            graph (Task 2.2 deferred). */}
        <ProjectionRail graphNodes={graphNodes} />

        {/* Composition picker — modal over the dimmed ambient canvas
            (sprint-236 Task 236.4/236.5). Selecting a scoped composition
            navigates to /theatres/:id/build (the existing scoped route keeps
            driving the graph); the chrome chip's × navigates back to
            /environment — ambient Global Intelligence always underneath. */}
        <CompositionPickerOverlay
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(theatre) => {
            setPickerOpen(false);
            navigate(`/theatres/${theatre.id}/build`);
          }}
        />
      </div>

      {/* Bottom lineage timeline (sprint-237, sdd.md §4.2.2 — re-opened Q3
          RESOLVED by the ambient model): present on BOTH the ambient surface
          (global-container runs; honest-empty when the container is unset or
          has no runs) and the scoped /theatres/:id/build route (theatre_id-
          filtered). Sibling row below the canvas — a runs-query failure
          never blocks the graph above. */}
      <LineageTimelineStrip
        filters={activeTheatreId ? { theatre_id: activeTheatreId, limit: 50 } : { limit: 50 }}
        enabled={!!activeTheatreId}
      />
    </div>
  );
}

// ── Projection rail (cycle-102 sprint-3 Tasks 3.1 + 3.3) ──────────────

function ProjectionRail({ graphNodes }: { graphNodes: D3GraphNode[] }) {
  const { investigations } = useInvestigationList();
  const projectableIds = collectProjectedInvestigationIds({
    investigations,
    graphNodes,
  });

  return (
    <aside
      role="complementary"
      aria-label="Workspace projection rail"
      data-testid="projection-rail"
      className="absolute right-4 top-20 z-20 w-[320px] max-h-[80vh] overflow-y-auto flex flex-col gap-3"
    >
      <section
        role="region"
        aria-label="Projected investigations"
        data-testid="projection-rail-investigations"
        className="flex flex-col gap-2 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-card)]/80 p-3"
      >
        <header className="text-[10px] font-mono uppercase tracking-widest text-[var(--fg-muted)]">
          Investigations · projected
        </header>
        {projectableIds.length === 0 && (
          <p
            role="status"
            className="text-[10px] font-mono text-[var(--fg-muted)]"
          >
            No active investigations.
          </p>
        )}
        {projectableIds.slice(0, 5).map((id) => (
          <InvestigationNode key={id} investigationId={id} />
        ))}
      </section>

      <ForgeNodeList />
    </aside>
  );
}
